import axios from 'axios'
import { clearSignedInPackageDrafts } from '@/lib/signedInPackagePurchase'

const ACCESS_TOKEN_KEY = 'auth_token'
const REFRESH_TOKEN_KEY = 'refresh_token'
const REFRESH_LOCK_NAME = 'metrly-auth-refresh'
const DEFAULT_MIN_VALIDITY_SECONDS = 60

export type SessionTokenResult =
  | { status: 'available'; accessToken: string }
  | { status: 'expired' }
  | { status: 'unavailable' }

type LockManagerLike = {
  request<T>(name: string, callback: () => Promise<T>): Promise<T>
}

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage
  } catch {
    return null
  }
}

function tokenExpiresAt(token: string): number | null {
  try {
    const payload = token.split('.')[1]
    if (!payload) return null
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=')
    const decoded = JSON.parse(atob(padded)) as { exp?: number }
    return typeof decoded.exp === 'number' ? decoded.exp * 1000 : null
  } catch {
    return null
  }
}

function isFresh(token: string | null, minValiditySeconds: number): token is string {
  if (!token) return false
  const expiresAt = tokenExpiresAt(token)
  return expiresAt === null || expiresAt - Date.now() > minValiditySeconds * 1000
}

class AuthSession {
  private refreshPromise: Promise<SessionTokenResult> | null = null

  getAccessToken(): string | null {
    return storage()?.getItem(ACCESS_TOKEN_KEY) || null
  }

  getRefreshToken(): string | null {
    return storage()?.getItem(REFRESH_TOKEN_KEY) || null
  }

  storeCredentials(accessToken: string, refreshToken: string | null | undefined): void {
    const target = storage()
    if (!target || !accessToken) return
    target.setItem(ACCESS_TOKEN_KEY, accessToken)
    if (refreshToken) {
      target.setItem(REFRESH_TOKEN_KEY, refreshToken)
    } else {
      target.removeItem(REFRESH_TOKEN_KEY)
    }
  }

  clearCredentials(): void {
    const target = storage()
    target?.removeItem(ACCESS_TOKEN_KEY)
    target?.removeItem(REFRESH_TOKEN_KEY)
    clearSignedInPackageDrafts()
  }

  expireSession(): void {
    this.clearCredentials()
    try {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('peltast:unauthorized'))
      }
    } catch {
      // The auth store will reconcile on the next protected navigation.
    }
  }

  async getValidAccessToken(minValiditySeconds = DEFAULT_MIN_VALIDITY_SECONDS): Promise<SessionTokenResult> {
    const current = this.getAccessToken()
    if (isFresh(current, minValiditySeconds)) {
      return { status: 'available', accessToken: current }
    }
    return this.refreshAccessToken({ failedAccessToken: current, minValiditySeconds })
  }

  async authenticatedFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
    const tokenResult = await this.getValidAccessToken()
    const accessToken = tokenResult.status === 'available'
      ? tokenResult.accessToken
      : this.getAccessToken()
    const requestWithToken = (token: string | null) => {
      const headers = new Headers(init.headers)
      if (token) headers.set('Authorization', `Bearer ${token}`)
      return fetch(input, { ...init, headers })
    }

    let response = await requestWithToken(accessToken)
    if (response.status !== 401) return response

    const refreshed = await this.refreshAccessToken({ failedAccessToken: accessToken })
    if (refreshed.status === 'available') {
      response = await requestWithToken(refreshed.accessToken)
    } else if (refreshed.status === 'expired') {
      this.expireSession()
    }
    return response
  }

  async refreshAccessToken(options: { failedAccessToken?: string | null; minValiditySeconds?: number } = {}): Promise<SessionTokenResult> {
    if (this.refreshPromise) return this.refreshPromise
    const refreshPromise = this.runExclusive<SessionTokenResult>(REFRESH_LOCK_NAME, async (): Promise<SessionTokenResult> => {
      const minValiditySeconds = options.minValiditySeconds ?? DEFAULT_MIN_VALIDITY_SECONDS
      const latestAccessToken = this.getAccessToken()

      // Another tab may have refreshed while this request waited for the lock.
      if (
        latestAccessToken
        && latestAccessToken !== options.failedAccessToken
        && isFresh(latestAccessToken, minValiditySeconds)
      ) {
        return { status: 'available', accessToken: latestAccessToken }
      }

      const refreshToken = this.getRefreshToken()
      if (!refreshToken) return { status: 'expired' }

      try {
        const response = await axios.post('/hoard-auth/token/refresh', {
          refresh_token: refreshToken,
        }, {
          validateStatus: status => status < 500,
        })

        if (response.status === 200 && response.data?.access_token) {
          this.storeCredentials(
            response.data.access_token,
            response.data.refresh_token ?? refreshToken,
          )
          return { status: 'available', accessToken: response.data.access_token }
        }
        if ([400, 401, 403].includes(response.status)) return { status: 'expired' }
        return { status: 'unavailable' }
      } catch {
        // Network and server failures are recoverable. Preserve credentials so a
        // later request can retry instead of signing the user out.
        return { status: 'unavailable' }
      }
    })
    const activeRefresh = refreshPromise.finally(() => {
      this.refreshPromise = null
    })
    this.refreshPromise = activeRefresh
    return activeRefresh
  }

  async runExclusive<T>(name: string, callback: () => Promise<T>): Promise<T> {
    const locks = typeof navigator === 'undefined'
      ? undefined
      : (navigator as Navigator & { locks?: LockManagerLike }).locks
    return locks?.request ? locks.request(name, callback) : callback()
  }
}

export const authSession = new AuthSession()
export default authSession
